import type { GetServerSidePropsContext, InferGetStaticPropsType } from 'next';
import type { NextPageWithLayout } from '@/pages/_app';
import {
  getCatalogCategories,
  getCatalogCompanyByHandle,
  getCatalogCompanyReviews,
  getCatalogCompanyReviewSummary,
  getCatalogItems,
} from '@/lib/service/catalog';
import { CatalogCompany, CatalogCompanyType, CatalogItem } from '@/shared/types/catalog';
import truncate from 'lodash/truncate';
import { ParamKey } from '@/shared/types/url-params';
import { SupplierProfileProductsSort, SupplierProfileTab } from '@/shared/types/sourcing-marketplace';
import React, { useEffect, useRef, useState } from 'react';
import { logPageView } from '@/lib/service';
import { getSupplierName } from '@/shared/utils/sourcing-marketplace-util';
import CustomHead from '@/components/Common/Layout/Head';
import { WEBSITE_URL } from '@/shared/constants';
import { Layout } from '@/components/Common';
import Script from 'next/script';
import { getBreadcrumbListJsonLd, getProductItemListJsonLd } from '@/shared/seo/jsonld';
import CapabilitiesTab from '@/components/SourcingMarketplace/FactoryDetail/CapabilitiesTab';
import ReviewsTab from '@/components/SourcingMarketplace/FactoryDetail/ReviewsTab';
import './index.less';
import SidebarLeft from '@/components/SourcingMarketplace/FactoryDetail/SidebarLeft';
import SourcingBreadcrumbs from '@/components/SourcingMarketplace/Common/SourcingBreadcrumbs';
import { useHistoryList } from '@/lib/context/HistoryListProvider';
import ProfileTab from '@/components/SourcingMarketplace/FactoryDetail/ProfileTab';
import { isEmpty, toNumber } from 'lodash-es';
import {
  SUPPLIER_PRODUCTS_RECOMBEE_PAGE_SIZE,
  SUPPLIER_PRODUCTS_SEARCH_PAGE_SIZE,
} from '@/shared/constants/sourcing-marketplace';
import ProductsTab from '@/components/SourcingMarketplace/FactoryDetail/ProductsTab';
import { isMobileByUserAgent } from '@/shared/utils/utils';

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    const userAgent = context.req.headers['user-agent'];
    const isMobile = isMobileByUserAgent(userAgent);
    // #region - Get url params & declare variables
    const { handle } = context.params as { handle: string };
    const sort = context.query?.[ParamKey.SORT] || SupplierProfileProductsSort.FEATURED;
    const searchKey = context.query?.[ParamKey.SEARCH];
    const l3CategoryId = context.query?.[ParamKey.L3_CATEGORY_ID];
    const productsPageParam = context.query?.[ParamKey.PRODUCTS_SEARCH_PAGE];
    const productsPage = productsPageParam ? +productsPageParam : 0;
    const reviewPageParam = context.query?.[ParamKey.PAGE];
    const reviewPage = reviewPageParam ? +reviewPageParam : 0;
    // #endregion

    // #region - Fetch the supplier detail first
    const supplier: CatalogCompany = await getCatalogCompanyByHandle(handle);

    if (!supplier.enabledAt || supplier.isArchived) {
      return { notFound: true };
    }
    // #endregion

    // #region - Prepare to get data
    // 1. Get supplier reviews data
    const getReviews = () =>
      getCatalogCompanyReviews({
        page: reviewPage ? reviewPage : 0,
        size: 20,
        companyId: supplier.id,
        sorts: '-createdAt',
      });

    const getReviewSummary = () => getCatalogCompanyReviewSummary(supplier.id);

    // 2.Get all l3 categories that the supplier related to
    const getAllL3Categories = () =>
      getCatalogCategories({
        page: 0,
        size: supplier?.categoryIds?.length ?? 0,
        includeIds: supplier?.categoryIds ?? [],
      });

    // 3. Get the products list
    const getFeaturedProducts = () =>
      getCatalogItems({
        catalogCategoryId: l3CategoryId ? [toNumber(l3CategoryId)] : null,
        catalogCompanyId: [toNumber(supplier?.id)],
        size: SUPPLIER_PRODUCTS_RECOMBEE_PAGE_SIZE,
        page: 0,
      });

    const getSearchedProducts = () =>
      getCatalogItems({
        catalogCompanyId: [toNumber(supplier?.id)],
        catalogCategoryId: l3CategoryId ? [toNumber(l3CategoryId)] : null,
        keyword: searchKey as string,
        page: productsPage,
        sorts: sort === SupplierProfileProductsSort.NEW ? '-enabledAt' : '-rank',
        size: SUPPLIER_PRODUCTS_SEARCH_PAGE_SIZE,
      });

    // #endregion

    // #region - Do api requests
    const isFeaturedSort = sort === SupplierProfileProductsSort.FEATURED && isEmpty(searchKey);

    const [reviewsResponse, reviewSummaryResponse, allL3CategoriesResponse, productsResponse] = await Promise.all([
      getReviews(),
      getReviewSummary(),
      getAllL3Categories(),
      isFeaturedSort ? getFeaturedProducts() : getSearchedProducts(),
    ]);
    // #endregion

    context.res.setHeader('Cache-Control', 's-maxage=60, stale-while-revalidate');

    return {
      props: {
        supplier,
        products: productsResponse?.list,
        productsPage,
        productsTotal: (productsResponse as ListResponse<CatalogItem>)?.total ?? 0,
        categoryList: allL3CategoriesResponse?.list,
        reviewsList: reviewsResponse?.list,
        reviewSummary: reviewSummaryResponse,
        reviewTotal: reviewsResponse?.total,
        reviewPage: reviewPage,
        tab: context.query?.['tab'] ?? '',
        isMobile,
      },
    };
  } catch (e) {
    return { notFound: true };
  }
}

const getPageTitle = (supplier: CatalogCompany) => {
  const isServices = supplier?.type === CatalogCompanyType.SPECIALIST;
  return `${getSupplierName(supplier)} - ${
    isServices ? 'Custom services' : 'Custom projects & base products'
  } - Vetted | Pietra`;
};

const FactoryDetailPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = ({
  supplier,
  products,
  productsPage,
  productsTotal,
  categoryList,
  reviewsList,
  reviewSummary,
  reviewTotal,
  reviewPage,
  isMobile,
}) => {
  const [activeTab, setActiveTab] = useState(SupplierProfileTab.PROFILE_INFO);
  const profileContentRef = useRef(null);
  const productsContentRef = useRef(null);
  const capabilitiesContentRef = useRef(null);
  const reviewsContentRef = useRef(null);
  const tabsContainerRef = useRef(null);
  const activeTabRef = useRef(null);
  const { sourcingBreadcrumbs, setSourcingBreadcrumbs } = useHistoryList();
  const supplierName = getSupplierName(supplier);

  useEffect(() => {
    if (supplier?.name) {
      logPageView({
        pageUrl: window.location.href || '',
        routeName: window.location.pathname,
        pageTitle: getPageTitle(supplier),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier?.name]);

  useEffect(() => {
    if (supplier) {
      const breadcrumbs = sourcingBreadcrumbs.filter((breadcrumb) => breadcrumb.type !== 'supplierName');
      setSourcingBreadcrumbs([
        ...breadcrumbs,
        {
          type: 'supplierName',
          label: supplier?.name,
          supplierId: supplier?.handle,
        },
      ]);
    }
  }, [supplier]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveTab(entry.target.id as SupplierProfileTab);
          }
        });
      },
      { threshold: 0.35 },
    );

    // observe the tab contents
    if (profileContentRef && profileContentRef.current) observer.observe(profileContentRef.current);
    if (productsContentRef && productsContentRef.current) observer.observe(productsContentRef.current);
    if (capabilitiesContentRef && capabilitiesContentRef.current) observer.observe(capabilitiesContentRef.current);
    if (reviewsContentRef && reviewsContentRef.current) observer.observe(reviewsContentRef.current);

    return () => {
      observer.disconnect();
    };
  }, [profileContentRef, productsContentRef, capabilitiesContentRef, reviewsContentRef]);

  useEffect(() => {
    scrollToSelectedTab();
  }, [activeTab]);

  const getContentRef = (itemId: SupplierProfileTab) => {
    switch (itemId) {
      case SupplierProfileTab.PROFILE_INFO:
        return profileContentRef;
      case SupplierProfileTab.BASE_PRODUCTS:
        return productsContentRef;
      case SupplierProfileTab.CAPABILITIES:
        return capabilitiesContentRef;
      case SupplierProfileTab.REVIEWS:
        return reviewsContentRef;
      default:
        return null;
    }
  };

  const scrollToSelectedContent = (itemId: SupplierProfileTab) => {
    const currentRef = getContentRef(itemId);
    const parentEle = isMobile
      ? document.querySelector('.supplier-detail-layout')
      : document.querySelector('.primary-content');

    const topOffset = isMobile ? 180 : 200;

    if (currentRef && parentEle) {
      parentEle.scrollTo({
        top: currentRef.current.offsetTop - topOffset,
        behavior: 'smooth',
      });
    }
  };

  const scrollToSelectedTab = () => {
    const activeTabEle = activeTabRef.current;
    const scrollContainerEle = tabsContainerRef.current;

    if (activeTabEle && scrollContainerEle) {
      const elementLeft = activeTabEle.offsetLeft;
      const elementWidth = activeTabEle.clientWidth;
      const containerWidth = scrollContainerEle.clientWidth;
      scrollContainerEle.scrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2;
    }
  };

  const clickTab = (itemId: SupplierProfileTab) => {
    setActiveTab(itemId);

    // Scroll to the top of the selected tab content
    scrollToSelectedContent(itemId);
  };

  const profileTabContent = <ProfileTab supplier={supplier} />;

  const productsTabContent = (
    <ProductsTab
      page={productsPage}
      total={productsTotal}
      L3Categories={categoryList}
      products={products}
      supplier={supplier}
    />
  );

  const capabilitiesTabContent = (
    <CapabilitiesTab
      isMobile={isMobile}
      supplier={supplier}
      categoryList={categoryList}
      onReviewLabelClick={() => clickTab(SupplierProfileTab.REVIEWS)}
    />
  );

  const reviewsTabContent = (
    <ReviewsTab
      reviewsList={reviewsList}
      reviewSummary={reviewSummary}
      total={reviewTotal}
      page={reviewPage}
      supplierName={supplierName}
    />
  );

  const tabs = [
    {
      itemId: SupplierProfileTab.PROFILE_INFO,
      name: `Profile`,
      children: profileTabContent,
    },
    {
      itemId: SupplierProfileTab.BASE_PRODUCTS,
      name: 'Listings',
      children: productsTabContent,
    },
    {
      itemId: SupplierProfileTab.CAPABILITIES,
      name: 'Capabilities',
      children: capabilitiesTabContent,
    },
    {
      itemId: SupplierProfileTab.REVIEWS,
      name: 'Reviews',
      children: reviewsTabContent,
    },
  ];

  return (
    <div className="supplier-detail-layout">
      <SidebarLeft supplier={supplier} onReviewLabelClick={() => clickTab(SupplierProfileTab.REVIEWS)} />
      <div className="primary-content">
        <div className="sticky-tabs" ref={tabsContainerRef}>
          {tabs.map((tab) => {
            return (
              <div
                ref={tab.itemId === activeTab ? activeTabRef : null}
                key={tab.itemId}
                className={tab.itemId === activeTab ? 'active' : ''}
                onClick={() => clickTab(tab.itemId)}
              >
                {tab.name}
              </div>
            );
          })}
        </div>
        {tabs.map((tab) => {
          return (
            <div key={tab.itemId} id={`${tab.itemId}`} ref={getContentRef(tab.itemId)} className="tab-body">
              {tab.children}
            </div>
          );
        })}
      </div>
    </div>
  );
};

FactoryDetailPage.getLayout = function getLayout(page: React.ReactElement) {
  const { supplier, products, isMobile } = page.props;
  const truncatedDescription = truncate(supplier.bio, {
    length: 160,
    omission: '...',
  });

  return (
    <>
      <CustomHead
        title={getPageTitle(supplier)}
        description={truncatedDescription}
        url={`${WEBSITE_URL}/sourcing/suppliers/${supplier.id}`}
        image={supplier.avatarUrl}
      />
      <Layout hideFooter>
        <div className="supplier-detail-layout-box">
          <SourcingBreadcrumbs usedLocation="FDP" isMobileFromServer={isMobile} />
          {page}
        </div>

        <Script
          id="product-jsonld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify([
              getProductItemListJsonLd(products),
              getBreadcrumbListJsonLd([{ id: `${WEBSITE_URL}/sourcing`, name: 'Browse all categories' }]),
            ]),
          }}
        />
      </Layout>
    </>
  );
};

export default FactoryDetailPage;
